import { useNotes } from '@/Context/NotesContext';
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useClickAway, useKey } from 'react-use';
import { Editor } from '@tinymce/tinymce-react';
import classNames from 'classnames';
import toastr from 'toastr';
import { useLocation } from 'react-use';

export const NotesModal = () => {
  const CREATION_MODE = {
    NEW: 'new',
    EXISTING: 'existing'
  };

  const [creationMode, setCreationMode] = useState(CREATION_MODE.NEW);
  const [title, setTitle] = useState('');
  const [currentNote, setCurrentNote] = useState('');
  const [activeSelect, setActiveSelect] = useState(false);
  const [selectTitle, setSelectTitle] = useState('Select Note');
  const [viewNoteId, setViewNoteId] = useState(null);
  const [viewNoteTitle, setViewNoteTitle] = useState('');
  const [viewNoteContent, setViewNoteContent] = useState('');
  const notesModalRef = useRef(null);
  const selectRef = useRef(null);
  const newNoteTitleRef = useRef(null);
  const location = useLocation()?.href;
  const [isSaving, setIsSaving] = useState(false);

  const {
    showNotesModal,
    setShowNotesModal,
    selectedText,
    setSelectedText,
    notesMode,
    setNotesMode,
    brandName,
    brandId,
    setExistingNotes,
    existingNotes,
    loadExistingNotes,
    MODE,
    clientId
  } = useNotes();

  const getSelectionContext = () => {
    const context = {
      page_title: document.title,
      timestamp: new Date().toISOString(),
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight
    };

    // Try to get section or container info
    try {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const container = range.commonAncestorContainer;
        const element =
          container.nodeType === 3 ? container.parentNode : container;

        // Try to find a parent with an ID or meaningful class
        let contextElement = element;
        let depth = 0;
        const maxDepth = 5; // Don't go too far up the DOM tree

        while (contextElement && depth < maxDepth) {
          if (contextElement.id) {
            context.container_id = contextElement.id;
            break;
          }

          if (
            contextElement.className &&
            typeof contextElement.className === 'string'
          ) {
            // Filter out utility classes
            const classes = contextElement.className
              .split(' ')
              .filter(
                cls =>
                  cls.length > 3 && !cls.startsWith('_') && !cls.includes('-')
              );

            if (classes.length > 0) {
              context.container_class = classes[0];
              break;
            }
          }

          // Try to get heading text as context
          if (
            ['H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(
              contextElement.tagName
            )
          ) {
            context.nearest_heading = contextElement.textContent
              .trim()
              .substring(0, 100);
            break;
          }

          contextElement = contextElement.parentNode;
          depth++;
        }
      }
    } catch (e) {
      // If anything goes wrong, just continue without the additional context
      console.error('Error getting selection context:', e);
    }

    return context;
  };

  const handleSelectOption = e => {
    e.stopPropagation();
    const target = e.target;

    if (!existingNotes || existingNotes.length === 0) {
      setActiveSelect(false);
      return;
    }

    if (
      target.classList.contains('existing-note-select-inner-item') ||
      target.closest('.existing-note-select-inner-item')
    ) {
      const title = target.classList.contains('existing-note-select-inner-item')
        ? target.querySelector('span').textContent
        : target
            .closest('.existing-note-select-inner-item')
            .querySelector('span').textContent;
      const noteId = target.classList.contains(
        'existing-note-select-inner-item'
      )
        ? target.querySelector('input[type="radio"]').value
        : target
            .closest('.existing-note-select-inner-item')
            .querySelector('input[type="radio"]').value;

      setActiveSelect(false);
      setSelectTitle(title);
      setCurrentNote(noteId);
      setViewNoteId(noteId);
    }
  };

  const stripHtmlTags = text => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = text;
    const textContent = tmp.textContent || tmp.innerText || '';

    tmp.remove();

    return textContent;
  };

  useEffect(() => {
    if (!showNotesModal || !selectedText) {
      return;
    }

    let contentToInject = selectedText;

    if (!contentToInject.startsWith('<p>')) {
      contentToInject = '<p>' + contentToInject + '</p>';
    }

    setSelectedText(contentToInject);

    return () => {
      setSelectedText('');
    };
  }, [showNotesModal, selectedText]);

  useEffect(() => {
    if (showNotesModal && selectedText) {
      if (selectedText) {
        const words = stripHtmlTags(selectedText).split(' ');
        const firstFewWords = words?.slice(0, 5).join(' ');
        setTitle(firstFewWords + (words.length > 5 ? '...' : ''));
      }
    }

    return () => {
      setTitle('');
    };
  }, [showNotesModal]);

  const handleEditorChange = content => {
    setSelectedText(content);
    window.tinymce.activeEditor.fire('ResizeEditor');
  };

  const handleEditorChangeView = content => {
    setViewNoteContent(content);
  };

  const handleCancel = () => {
    setShowNotesModal(false);
    setCreationMode(CREATION_MODE.NEW);
    setTitle('');
    setSelectTitle('Select Note');
  };

  useClickAway(notesModalRef, handleCancel);

  useKey('Escape', handleCancel);

  useClickAway(selectRef, () => {
    setActiveSelect(false);
  });

  const showSuccessNotification = message => {
    toastr.success(message);
  };

  const handleError = e => {
    toastr.error(typeof e === 'string' ? e : Object.values(e).join('\n'));
  };

  const handleSave = () => {
    const isNewNote =
      creationMode === CREATION_MODE.NEW || notesMode === MODE.CREATE_NEW_NOTE;

    if (!brandId) {
      return;
    }

    if (!selectedText && !viewNoteContent) {
      alert('Please enter the note text.');
      return;
    }

    if (isSaving) {
      return;
    }

    if (isNewNote) {
      if (!title) {
        alert('Please provide a title for your note.');
        return;
      }

      setIsSaving(true);

      const notesEndpoint = route('notes.store', {
        client: clientId,
        brand: brandId
      });

      fetch(notesEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: title,
          content: selectedText,
          selected_text: stripHtmlTags(selectedText),
          selected_html: selectedText,
          metadata: {
            source_url: window.location.href,
            selection_context: getSelectionContext()
          }
        })
      })
        .then(response => response.json())
        .then(data => {
          setIsSaving(false);

          if (data.error || data.errors) {
            return handleError(data.error || data.errors);
          }

          setExistingNotes(prev => {
            return [
              {
                id: data.note.id,
                title: data.note.title,
                date: data.note.date,
                content: data.note.note
              },
              ...prev
            ];
          });

          showSuccessNotification('Note saved successfully.');

          if (notesMode === MODE.CREATE_NEW_NOTE) {
            setNotesMode(MODE.LIST_NOTES);
          } else {
            handleCancel();
          }
        });
    } else {
      if (!currentNote) {
        alert('Please select a note to add to.');
        return;
      }

      setIsSaving(true);

      const updateEndpoint = route('notes.addToExistingNote', {
        client: clientId,
        brand: brandId,
        note: currentNote
      });

      fetch(updateEndpoint, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          content: selectedText,
          selected_text: stripHtmlTags(selectedText),
          selected_html: selectedText,
          metadata: {
            update_source_url: window.location.href,
            update_timestamp: new Date().toISOString(),
            update_context: getSelectionContext()
          }
        })
      })
        .then(response => response.json())
        .then(data => {
          setIsSaving(false);

          if (data.error || data.errors) {
            return handleError(data.error || data.errors);
          }

          showSuccessNotification('Note updated successfully.');
          handleCancel();
          setExistingNotes(prev => {
            return prev.map(note => {
              if (parseInt(note.id) === parseInt(viewNoteId)) {
                return {
                  ...note,
                  title: data.note.title,
                  content: data.note.note,
                  date: data.note.date
                };
              }

              return note;
            });
          });
        });
    }
  };

  useEffect(() => {
    loadExistingNotes();
  }, [location]);

  const handleSaveSingleNote = () => {
    if (!viewNoteContent) {
      alert('Please enter the note text.');
      return;
    }

    if (!viewNoteId) {
      alert('Please select a note to add to.');
      return;
    }

    if (!viewNoteTitle) {
      alert('Please provide a title for your note.');
      return;
    }

    if (isSaving) {
      return;
    }

    setIsSaving(true);

    const updateEndpoint = route('notes.updateOriginalNote', {
      client: clientId,
      noteId: viewNoteId
    });

    fetch(updateEndpoint, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        title: viewNoteTitle,
        content: viewNoteContent,
        selected_text: stripHtmlTags(viewNoteContent),
        selected_html: viewNoteContent,
        metadata: {
          update_timestamp: new Date().toISOString()
        }
      })
    })
      .then(response => response.json())
      .then(data => {
        setIsSaving(false);

        if (data.error || data.errors) {
          return handleError(data.error || data.errors);
        }

        showSuccessNotification('Note saved successfully.');
        handleCancel();
        setExistingNotes(prev => {
          return prev.map(note => {
            if (parseInt(note.id) === parseInt(viewNoteId)) {
              return {
                ...note,
                title: data.note.title,
                content: data.note.note,
                date: data.note.date
              };
            }

            return note;
          });
        });
      });
  };

  return (
    showNotesModal &&
    createPortal(
      <div className="notes-modal" ref={notesModalRef}>
        <div className="notes-modal-content">
          <div className="notes-modal-help">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <mask
                id="mask0_2608_995"
                style={{ maskType: 'alpha' }}
                maskUnits="userSpaceOnUse"
                x="0"
                y="0"
                width="20"
                height="20"
              >
                <rect width="20" height="20" fill="#D9D9D9" />
              </mask>
              <g mask="url(#mask0_2608_995)">
                <path
                  d="M9.37504 13.959H10.625V9.16732H9.37504V13.959ZM10 7.74107C10.1907 7.74107 10.3506 7.67655 10.4796 7.54753C10.6087 7.4185 10.6732 7.25864 10.6732 7.06794C10.6732 6.87725 10.6087 6.71739 10.4796 6.58836C10.3506 6.45947 10.1907 6.39503 10 6.39503C9.80935 6.39503 9.64949 6.45947 9.52046 6.58836C9.39143 6.71739 9.32692 6.87725 9.32692 7.06794C9.32692 7.25864 9.39143 7.4185 9.52046 7.54753C9.64949 7.67655 9.80935 7.74107 10 7.74107ZM10.0015 17.9173C8.9065 17.9173 7.87726 17.7095 6.91379 17.294C5.95032 16.8784 5.11226 16.3145 4.39962 15.6021C3.68698 14.8897 3.12275 14.052 2.70692 13.089C2.29122 12.1259 2.08337 11.097 2.08337 10.0021C2.08337 8.90711 2.29115 7.87787 2.70671 6.9144C3.12226 5.95093 3.68622 5.11287 4.39858 4.40023C5.11094 3.6876 5.94865 3.12336 6.91171 2.70753C7.87476 2.29183 8.90372 2.08398 9.99858 2.08398C11.0936 2.08398 12.1228 2.29176 13.0863 2.70732C14.0498 3.12287 14.8878 3.68683 15.6005 4.39919C16.3131 5.11155 16.8773 5.94926 17.2932 6.91232C17.7089 7.87537 17.9167 8.90433 17.9167 9.99919C17.9167 11.0942 17.7089 12.1234 17.2934 13.0869C16.8778 14.0504 16.3139 14.8884 15.6015 15.6011C14.8891 16.3137 14.0514 16.8779 13.0884 17.2938C12.1253 17.7095 11.0964 17.9173 10.0015 17.9173Z"
                  fill="#CA3982"
                />
              </g>
            </svg>

            <p>
              You can select text from any of your work and Magnolia will
              display a “Save as Note” button in the upper right of your screen.
            </p>
          </div>

          <button onClick={handleCancel} className="notes-modal-exit">
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.33317 11.0846L0.916504 10.668L5.58317 6.0013L0.916504 1.33464L1.33317 0.917969L5.99984 5.58464L10.6665 0.917969L11.0832 1.33464L6.4165 6.0013L11.0832 10.668L10.6665 11.0846L5.99984 6.41797L1.33317 11.0846Z"
                fill="#1C1B1F"
              />
            </svg>
          </button>

          {notesMode === MODE.CREATE_FROM_SELECTION && (
            <>
              <div className="notes-modal-form">
                <div className="form-group note-type-toggle">
                  <div className="toggle-options">
                    <label
                      className={classNames('toggle-option', {
                        active: creationMode === CREATION_MODE.NEW
                      })}
                    >
                      <input
                        type="radio"
                        name="note-type"
                        value="new"
                        checked={creationMode === CREATION_MODE.NEW}
                        onChange={() => setCreationMode(CREATION_MODE.NEW)}
                      />
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <mask
                          id="mask0_2500_7362"
                          style={{ maskType: 'alpha' }}
                          maskUnits="userSpaceOnUse"
                          x="0"
                          y="0"
                          width="20"
                          height="20"
                        >
                          <rect width="20" height="20" fill="#D9D9D9" />
                        </mask>
                        <g mask="url(#mask0_2500_7362)">
                          <path
                            d="M4.83334 16.4173C4.48612 16.4173 4.19098 16.2958 3.94793 16.0527C3.70487 15.8097 3.58334 15.5145 3.58334 15.1673V4.83398C3.58334 4.48676 3.70487 4.19162 3.94793 3.94857C4.19098 3.70551 4.48612 3.58398 4.83334 3.58398H15.1667C15.5139 3.58398 15.809 3.70551 16.0521 3.94857C16.2951 4.19162 16.4167 4.48676 16.4167 4.83398V9.60482C16.3056 9.57704 16.2049 9.55621 16.1146 9.54232C16.0243 9.52843 15.9306 9.5076 15.8333 9.47982V4.83398C15.8333 4.66732 15.7639 4.51454 15.625 4.37565C15.4861 4.23676 15.3333 4.16732 15.1667 4.16732H4.83334C4.66668 4.16732 4.5139 4.23676 4.37501 4.37565C4.23612 4.51454 4.16668 4.66732 4.16668 4.83398V15.1673C4.16668 15.334 4.23612 15.4868 4.37501 15.6257C4.5139 15.7645 4.66668 15.834 4.83334 15.834H9.45834C9.47223 15.9451 9.48612 16.0458 9.50001 16.1361C9.5139 16.2263 9.53473 16.3201 9.56251 16.4173H4.83334ZM4.16668 15.834V4.16732V9.47982V9.41732V15.834ZM6.37501 13.3757H9.64584C9.67362 13.2784 9.70834 13.1847 9.75001 13.0944C9.79168 13.0041 9.83334 12.9034 9.87501 12.7923H6.37501V13.3757ZM6.37501 10.2923H11.9792C12.2014 10.1673 12.4028 10.0597 12.5833 9.9694C12.7639 9.87912 12.9583 9.79926 13.1667 9.72982V9.70898H6.37501V10.2923ZM6.37501 7.20898H13.625V6.62565H6.37501V7.20898ZM15 18.209C14.1389 18.209 13.4097 17.9104 12.8125 17.3132C12.2153 16.7159 11.9167 15.9868 11.9167 15.1257C11.9167 14.2645 12.2153 13.5354 12.8125 12.9382C13.4097 12.3409 14.1389 12.0423 15 12.0423C15.8611 12.0423 16.5903 12.3409 17.1875 12.9382C17.7847 13.5354 18.0833 14.2645 18.0833 15.1257C18.0833 15.9868 17.7847 16.7159 17.1875 17.3132C16.5903 17.9104 15.8611 18.209 15 18.209ZM14.7083 17.5007H15.2917V15.4173H17.375V14.834H15.2917V12.7507H14.7083V14.834H12.625V15.4173H14.7083V17.5007Z"
                            fill="#1C1B1F"
                          />
                        </g>
                      </svg>

                      <span>New Note</span>
                    </label>
                    <label
                      className={classNames('toggle-option', {
                        active: creationMode === 'existing'
                      })}
                    >
                      <input
                        type="radio"
                        name="note-type"
                        value="existing"
                        checked={creationMode === 'existing'}
                        onChange={() => setCreationMode('existing')}
                      />
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <mask
                          id="mask0_2500_7335"
                          style={{ maskType: 'alpha' }}
                          maskUnits="userSpaceOnUse"
                          x="0"
                          y="0"
                          width="20"
                          height="20"
                        >
                          <rect width="20" height="20" fill="#D9D9D9" />
                        </mask>
                        <g mask="url(#mask0_2500_7335)">
                          <path
                            d="M9.70831 16.4173V10.2923H3.58331V9.70898H9.70831V3.58398H10.2916V9.70898H16.4166V10.2923H10.2916V16.4173H9.70831Z"
                            fill="#1C1B1F"
                          />
                        </g>
                      </svg>

                      <span>Add To Existing Note</span>
                    </label>
                  </div>
                </div>

                <div className="form-fields">
                  {creationMode === CREATION_MODE.NEW && (
                    <div className="form-group new-note-title">
                      <input
                        id="note-title"
                        type="text"
                        className="form-control"
                        placeholder="Your Note Title"
                        defaultValue={title}
                        onChange={e => setTitle(e.target.value)}
                      />
                    </div>
                  )}

                  {creationMode === 'existing' && (
                    <div
                      className={classNames('form-group existing-note-select', {
                        active: activeSelect
                      })}
                      ref={selectRef}
                      onClick={() => setActiveSelect(!activeSelect)}
                    >
                      <div className="existing-note-select-inner">
                        <div className="existing-note-select-inner-title">
                          <span>{selectTitle}</span>
                        </div>
                        <div className="existing-note-select-inner-items">
                          {existingNotes &&
                            existingNotes.map(note => (
                              <label
                                className="existing-note-select-inner-item"
                                key={note.id}
                                onClick={handleSelectOption}
                              >
                                <input
                                  type="radio"
                                  name="existing-note"
                                  value={note.id}
                                />
                                <span>{note.title}</span>
                              </label>
                            ))}

                          {(!existingNotes || existingNotes.length === 0) && (
                            <label
                              className="existing-note-select-inner-item"
                              key="0"
                              onClick={handleSelectOption}
                            >
                              <input
                                type="radio"
                                name="existing-note"
                                value="0"
                              />
                              <span>No existing notes</span>
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="form-group editor-group">
                    <div
                      id="note-content-wrapper mce-activated"
                      className="wysiwyg-container"
                    >
                      <div id="note-content" className="form-control">
                        <Editor
                          value={selectedText}
                          tinymceScriptSrc="/js/tinymce.min.js"
                          onEditorChange={handleEditorChange}
                          init={{
                            selector: '#note-content',
                            placeholder: 'Enter your note here...',
                            inline: true,
                            menubar: false,
                            toolbar: false,
                            statusbar: false,
                            plugins: 'autoresize',
                            branding: false,
                            content_css: false,
                            relative_urls: false,
                            remove_script_host: false,
                            convert_urls: false,
                            remove_trailing_brs: false,
                            forced_root_block: 'p',
                            inline_styles: true,
                            verify_html: false,
                            cleanup: false,
                            valid_elements: '*[*]',
                            setup: editor => {
                              editor.on('init', () => {
                                editor.setContent(selectedText || '');

                                const body = editor.getBody();
                                if (!editor.getContent().trim() && body) {
                                  body.innerHTML = selectedText || '';
                                }

                                editor.focus();
                              });

                              editor.on('blur', () => {
                                setSelectedText(editor.getContent());
                              });
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="notes-modal-actions">
                    <button
                      className="button notes-modal-cancel"
                      onClick={handleCancel}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <mask
                          id="mask0_2500_7322"
                          style={{ maskType: 'alpha' }}
                          maskUnits="userSpaceOnUse"
                          x="0"
                          y="0"
                          width="20"
                          height="20"
                        >
                          <rect width="20" height="20" fill="#D9D9D9" />
                        </mask>
                        <g mask="url(#mask0_2500_7322)">
                          <path
                            d="M5.33332 15.0846L4.91666 14.668L9.58332 10.0013L4.91666 5.33464L5.33332 4.91797L9.99999 9.58464L14.6667 4.91797L15.0833 5.33464L10.4167 10.0013L15.0833 14.668L14.6667 15.0846L9.99999 10.418L5.33332 15.0846Z"
                            fill="#1C1B1F"
                          />
                        </g>
                      </svg>
                      <span>Cancel</span>
                    </button>
                    <button
                      id="notes-modal-save"
                      className="button"
                      onClick={handleSave}
                    >
                      Save Note
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}

          {notesMode === MODE.CREATE_FROM_EXISTING_NOTE && (
            <div className="notes-modal-edit">
              <div className="notes-modal-title">
                <button
                  onClick={() => setNotesMode(MODE.LIST_NOTES)}
                  className="button notes-modal-cancel"
                >
                  <svg
                    width="14"
                    height="16"
                    viewBox="0 0 14 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M13.6001 8H1.0001" stroke="#555555" />
                    <path
                      d="M8.20007 0.800049L1.00007 8.00005L8.20007 15.2"
                      stroke="#555555"
                    />
                  </svg>

                  <span>Back to List</span>
                </button>
              </div>

              <div className="notes-modal-edit-content">
                <input
                  type="text"
                  id="note-title"
                  className="form-control"
                  placeholder="Note Title"
                  defaultValue={viewNoteTitle}
                  onChange={e => setViewNoteTitle(e.target.value)}
                />

                <div className="form-group editor-group">
                  <div
                    id="note-content-wrapper mce-activated"
                    className="wysiwyg-container"
                  >
                    <div id="note-content" className="form-control">
                      {viewNoteId && (
                        <Editor
                          value={viewNoteContent}
                          tinymceScriptSrc="/js/tinymce.min.js"
                          onEditorChange={handleEditorChangeView}
                          init={{
                            selector: '#note-content-2',
                            placeholder: 'Enter your note here...',
                            inline: true,
                            menubar: false,
                            toolbar: false,
                            statusbar: false,
                            plugins: 'autoresize',
                            branding: false,
                            content_css: false,
                            relative_urls: false,
                            remove_script_host: false,
                            convert_urls: false,
                            remove_trailing_brs: false,
                            forced_root_block: 'p',
                            inline_styles: true,
                            verify_html: false,
                            cleanup: false,
                            valid_elements: '*[*]',
                            setup: editor => {
                              editor.on('init', () => {
                                editor.setContent(viewNoteContent || '');

                                const body = editor.getBody();
                                if (!editor.getContent().trim() && body) {
                                  body.innerHTML = viewNoteContent || '';
                                }

                                editor.focus();
                              });

                              editor.on('blur', () => {
                                setViewNoteContent(editor.getContent());
                              });
                            }
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div className="notes-modal-actions">
                  <button
                    className="button notes-modal-cancel"
                    onClick={handleCancel}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <mask
                        id="mask0_2500_7322"
                        style={{ maskType: 'alpha' }}
                        maskUnits="userSpaceOnUse"
                        x="0"
                        y="0"
                        width="20"
                        height="20"
                      >
                        <rect width="20" height="20" fill="#D9D9D9" />
                      </mask>
                      <g mask="url(#mask0_2500_7322)">
                        <path
                          d="M5.33332 15.0846L4.91666 14.668L9.58332 10.0013L4.91666 5.33464L5.33332 4.91797L9.99999 9.58464L14.6667 4.91797L15.0833 5.33464L10.4167 10.0013L15.0833 14.668L14.6667 15.0846L9.99999 10.418L5.33332 15.0846Z"
                          fill="#1C1B1F"
                        />
                      </g>
                    </svg>
                    <span>Cancel</span>
                  </button>
                  <button
                    id="notes-modal-save"
                    className="button"
                    onClick={handleSaveSingleNote}
                  >
                    Save Note
                  </button>
                </div>
              </div>
            </div>
          )}

          {notesMode === MODE.LIST_NOTES && (
            <div className="notes-modal-view">
              <div className="notes-modal-title">
                <h3>Your Notes for {brandName}</h3>

                <button
                  className="button"
                  onClick={() => {
                    setNotesMode(MODE.CREATE_NEW_NOTE);
                    setTitle('');
                    setSelectedText('');
                    setTimeout(() => newNoteTitleRef.current.focus(), 0);
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1.83337 13.4173C1.48615 13.4173 1.19101 13.2958 0.947957 13.0527C0.704902 12.8097 0.583374 12.5145 0.583374 12.1673V1.83398C0.583374 1.48676 0.704902 1.19162 0.947957 0.948568C1.19101 0.705512 1.48615 0.583984 1.83337 0.583984H12.1667C12.5139 0.583984 12.8091 0.705512 13.0521 0.948568C13.2952 1.19162 13.4167 1.48676 13.4167 1.83398V6.60482C13.3056 6.57704 13.2049 6.55621 13.1146 6.54232C13.0243 6.52843 12.9306 6.5076 12.8334 6.47982V1.83398C12.8334 1.66732 12.7639 1.51454 12.625 1.37565C12.4862 1.23676 12.3334 1.16732 12.1667 1.16732H1.83337C1.66671 1.16732 1.51393 1.23676 1.37504 1.37565C1.23615 1.51454 1.16671 1.66732 1.16671 1.83398V12.1673C1.16671 12.334 1.23615 12.4868 1.37504 12.6257C1.51393 12.7645 1.66671 12.834 1.83337 12.834H6.45837C6.47226 12.9451 6.48615 13.0458 6.50004 13.1361C6.51393 13.2263 6.53476 13.3201 6.56254 13.4173H1.83337ZM1.16671 12.834V1.16732V6.47982V6.41732V12.834ZM3.37504 10.3757H6.64587C6.67365 10.2784 6.70837 10.1847 6.75004 10.0944C6.79171 10.0041 6.83337 9.90343 6.87504 9.79232H3.37504V10.3757ZM3.37504 7.29232H8.97921C9.20143 7.16732 9.40282 7.05968 9.58337 6.9694C9.76393 6.87912 9.95837 6.79926 10.1667 6.72982V6.70898H3.37504V7.29232ZM3.37504 4.20898H10.625V3.62565H3.37504V4.20898ZM12 15.209C11.1389 15.209 10.4098 14.9104 9.81254 14.3132C9.21532 13.7159 8.91671 12.9868 8.91671 12.1257C8.91671 11.2645 9.21532 10.5354 9.81254 9.93815C10.4098 9.34093 11.1389 9.04232 12 9.04232C12.8612 9.04232 13.5903 9.34093 14.1875 9.93815C14.7848 10.5354 15.0834 11.2645 15.0834 12.1257C15.0834 12.9868 14.7848 13.7159 14.1875 14.3132C13.5903 14.9104 12.8612 15.209 12 15.209ZM11.7084 14.5007H12.2917V12.4173H14.375V11.834H12.2917V9.75065H11.7084V11.834H9.62504V12.4173H11.7084V14.5007Z"
                      fill="white"
                    />
                  </svg>

                  <span>New Note</span>
                </button>
              </div>

              <div className="notes-modal-view-list">
                {existingNotes &&
                  existingNotes.map(note => (
                    <div
                      className="notes-modal-view-list-item"
                      key={note.id}
                      onClick={() => {
                        setNotesMode(MODE.CREATE_FROM_EXISTING_NOTE);
                        setViewNoteId(note.id);
                        setViewNoteTitle(note.title);
                        setViewNoteContent(
                          note.content ? note.content : note.note
                        );
                      }}
                    >
                      <div className="note-title">
                        {note.title.length > 25
                          ? note.title.substring(0, 25) + '...'
                          : note.title}
                      </div>

                      <div className="note-date">{note.date}</div>
                    </div>
                  ))}

                {(!existingNotes || existingNotes.length === 0) && (
                  <p className="no-notes">No notes found for this brand</p>
                )}
              </div>
            </div>
          )}

          {notesMode === MODE.CREATE_NEW_NOTE && (
            <div className="notes-modal-edit">
              <div className="notes-modal-title">
                <button
                  onClick={() => setNotesMode(MODE.LIST_NOTES)}
                  className="button notes-modal-cancel"
                >
                  <svg
                    width="14"
                    height="16"
                    viewBox="0 0 14 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M13.6001 8H1.0001" stroke="#555555" />
                    <path
                      d="M8.20007 0.800049L1.00007 8.00005L8.20007 15.2"
                      stroke="#555555"
                    />
                  </svg>

                  <span>Back to List</span>
                </button>
              </div>

              <div className="notes-modal-edit-content">
                <input
                  type="text"
                  id="note-title"
                  className="form-control"
                  placeholder="Note Title"
                  defaultValue={title}
                  onChange={e => setTitle(e.target.value)}
                  ref={newNoteTitleRef}
                />

                <div className="form-group editor-group">
                  <div
                    id="note-content-wrapper mce-activated"
                    className="wysiwyg-container"
                  >
                    <div id="note-content" className="form-control">
                      <Editor
                        value={viewNoteContent}
                        tinymceScriptSrc="/js/tinymce.min.js"
                        onEditorChange={handleEditorChangeView}
                        init={{
                          selector: '#note-content-2',
                          placeholder: 'Enter your note here...',
                          inline: true,
                          menubar: false,
                          toolbar: false,
                          statusbar: false,
                          plugins: 'autoresize',
                          branding: false,
                          content_css: false,
                          relative_urls: false,
                          remove_script_host: false,
                          convert_urls: false,
                          remove_trailing_brs: false,
                          forced_root_block: 'p',
                          inline_styles: true,
                          verify_html: false,
                          cleanup: false,
                          valid_elements: '*[*]',
                          setup: editor => {
                            editor.on('init', () => {
                              editor.setContent(selectedText || '');

                              const body = editor.getBody();
                              if (!editor.getContent().trim() && body) {
                                body.innerHTML = selectedText || '';
                              }
                            });

                            editor.on('blur', () => {
                              setSelectedText(editor.getContent());
                            });
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="notes-modal-actions">
                  <button
                    className="button notes-modal-cancel"
                    onClick={handleCancel}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <mask
                        id="mask0_2500_7322"
                        style={{ maskType: 'alpha' }}
                        maskUnits="userSpaceOnUse"
                        x="0"
                        y="0"
                        width="20"
                        height="20"
                      >
                        <rect width="20" height="20" fill="#D9D9D9" />
                      </mask>
                      <g mask="url(#mask0_2500_7322)">
                        <path
                          d="M5.33332 15.0846L4.91666 14.668L9.58332 10.0013L4.91666 5.33464L5.33332 4.91797L9.99999 9.58464L14.6667 4.91797L15.0833 5.33464L10.4167 10.0013L15.0833 14.668L14.6667 15.0846L9.99999 10.418L5.33332 15.0846Z"
                          fill="#1C1B1F"
                        />
                      </g>
                    </svg>
                    <span>Cancel</span>
                  </button>
                  <button
                    id="notes-modal-save"
                    className="button"
                    onClick={handleSave}
                  >
                    Save Note
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>,
      document.body
    )
  );
};
